import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaPlus, FaTrash } from "react-icons/fa";
import "../assets/css/EditCustomer.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import QuoteGenerator from "../components/QuoteGenerator";

const CUSTOMER_STATUSES = ["Active", "Inactive"];
const CUSTOMER_TYPES = ["Organization", "Individual"];

// Helpers for splitting/stripping phone country codes
const splitPhoneWithCountryCode = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return { code: "", number: "" };

  // If there's a space, assume "<code> <number>"
  const spaceIdx = s.indexOf(" ");
  if (spaceIdx > 0) {
    const codePart = s.slice(0, spaceIdx);
    const numberPart = s.slice(spaceIdx + 1).trim();
    return { code: codePart, number: numberPart };
  }

  // If starts with + and is long enough, treat first 3 chars as code
  if (s.startsWith("+") && s.length > 3) {
    const codePart = s.slice(0, 3);
    const numberPart = s.slice(3).trim();
    return { code: codePart, number: numberPart };
  }

  // If it's only digits and longer than 10, treat leading as country code
  if (/^\d+$/.test(s) && s.length > 10) {
    const countryPart = s.slice(0, s.length - 10);
    const numberPart = s.slice(-10);
    const codePart = countryPart ? `+${countryPart}` : "";
    return { code: codePart, number: numberPart };
  }

  return { code: "", number: s };
};

const stripPhoneCountryCode = (raw) => {
  const { number } = splitPhoneWithCountryCode(raw);
  return number || String(raw || "").trim();
};

function EditCustomer({ clientId, onBack, onUpdate }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerType: "Organization",
    firstName: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    altPhone: "",
    companyName: "",
    domain: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    websiteDomainName: "",
    source: "",
    status: "",
    communicationLanguage: "",
    isQuoteSent: false,
    portalEnabled: true,
    hasPassword: false,
    portalPassword: "",
    services: [],
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    companyAddressLine1: "",
    companyAddressLine2: "",
    companyCity: "",
    companyState: "",
    companyCountry: "",
    companyZipCode: "",
    notes: ""
  });

  const [followupData, setFollowupData] = useState({
    followupDate: "",
    followupType: "",
    nextFollowupDate: "",
    remarks: ""
  });
  const [followupId, setFollowupId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [companyStates, setCompanyStates] = useState([]);
  const [companyCities, setCompanyCities] = useState([]);
  const [selectedCompanyCountryId, setSelectedCompanyCountryId] = useState(null);
  const [selectedCompanyStateId, setSelectedCompanyStateId] = useState(null);
  const [personalPhoneCountryCode, setPersonalPhoneCountryCode] = useState("");
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [sourceNameFromAPI, setSourceNameFromAPI] = useState(null);
  const [customServices, setCustomServices] = useState({});
  const [editingService, setEditingService] = useState(null);
  const [isAddingServiceToCategory, setIsAddingServiceToCategory] = useState(null);
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);

  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const res = await axiosInstance.get("/service/categories");
        if (res.data && Array.isArray(res.data)) setServiceCategories(res.data);
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  // Fetch domains for dropdown (same API as AddCustomer)
  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true);
      try {
        const res = await axiosInstance.get("/domains");
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (err) {
        console.error("Error fetching domains for customer (edit):", err);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const res = await axiosInstance.get("/lead-sources");
        if (res.data && Array.isArray(res.data)) setSources(res.data);
      } catch (err) {
        console.error("Error fetching sources:", err);
      }
    };
    fetchSources();
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await axiosInstance.get("/countries");
        if (res.data && Array.isArray(res.data)) setCountries(res.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!selectedCountryId) {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    axiosInstance
      .get(`/states/${selectedCountryId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setStates(res.data);
      })
      .catch((err) => {
        console.error("Error fetching states:", err?.response?.data?.message || err?.message || err);
        setStates([]);
      })
      .finally(() => setLoadingStates(false));
  }, [selectedCountryId]);

  useEffect(() => {
    if (!selectedStateId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    axiosInstance
      .get(`/cities/${selectedStateId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCities(res.data);
      })
      .catch((err) => {
        console.error("Error fetching cities:", err?.response?.data?.message || err?.message || err);
        setCities([]);
      })
      .finally(() => setLoadingCities(false));
  }, [selectedStateId]);

  useEffect(() => {
    if (!selectedCompanyCountryId) {
      setCompanyStates([]);
      return;
    }
    axiosInstance
      .get(`/states/${selectedCompanyCountryId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCompanyStates(res.data);
      })
      .catch(() => setCompanyStates([]));
  }, [selectedCompanyCountryId]);

  useEffect(() => {
    if (!selectedCompanyStateId) {
      setCompanyCities([]);
      return;
    }
    axiosInstance
      .get(`/cities/${selectedCompanyStateId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCompanyCities(res.data);
      })
      .catch(() => setCompanyCities([]));
  }, [selectedCompanyStateId]);

  useEffect(() => {
    if (!clientId) return;
    const fetchCustomer = async (categories) => {
      setLoading(true);
      try {
        let customerObj = null;
        let addressObj = {};
        let companyAddressObj = {};
        try {
          const res = await axiosInstance.get(`/client/${clientId}`);
          if (res.data) {
            customerObj = res.data.client || res.data;
            addressObj = res.data.address || {};
            companyAddressObj = res.data.company_address || {};
          }
        } catch (e) {
          console.error("Error fetching customer by ID:", e?.response?.data?.message || e?.message || e);
          const allRes = await axiosInstance.get("/clients");
          if (allRes.data && Array.isArray(allRes.data)) {
            const found = allRes.data.find(
              (item) => (item.client || item).client_id === clientId
            );
            if (found) {
              customerObj = found.customer || found;
              addressObj = found.address || {};
            }
          }
        }

        if (customerObj) {
          // Resolve service names to IDs if serviceCategories are already loaded
          const serviceNames = customerObj.Services || [];
          let resolvedServices = serviceNames;
          
          if (categories && categories.length > 0 && serviceNames.length > 0) {
            const allSubcategories = categories.flatMap(cat =>
              (cat.subcategories || [])
            );
            const resolvedIds = serviceNames
              .map(name => {
                // Try exact match first
                let found = allSubcategories.find(sub => sub.subcategory_name === name);
                // If not found, try case-insensitive match
                if (!found) {
                  found = allSubcategories.find(sub => 
                    sub.subcategory_name.toLowerCase() === name.toLowerCase()
                  );
                }
                return found ? found.subcategory_id : null;
              })
              .filter(Boolean);
            
            if (resolvedIds.length > 0) {
              resolvedServices = resolvedIds;
            }
          }
          
          // Handle Source - API returns Source as string name
          let sourceValue = "";
          if (customerObj.Source) {
            setSourceNameFromAPI(customerObj.Source);
            sourceValue = customerObj.Source;
          } else {
            setSourceNameFromAPI(null);
          }
          
          // Prefer explicit country code field from API if available; otherwise, fall back to splitting combined value
          const apiCountryCode = customerObj.Country_Code || customerObj.country_code || "";
          const personalSplit = apiCountryCode
            ? { code: apiCountryCode, number: (customerObj.Phone || customerObj.phone || "").toString().trim() }
            : splitPhoneWithCountryCode(customerObj.Phone || customerObj.phone || "");
          setPersonalPhoneCountryCode(personalSplit.code || "");

          setFormData({
            customerType: customerObj.Customer_Type || customerObj.customer_type || "Organization",
            firstName: customerObj.First_Name || "",
            lastName: customerObj.Last_Name || "",
            personalEmail: customerObj.Email || "",
            // Store only the raw number in state; country code is tracked separately
            personalPhone: personalSplit.number || "",
            altPhone: customerObj.Alt_Phone != null ? String(customerObj.Alt_Phone) : "",
            companyName: customerObj.Company_Name || "",
            domain: customerObj.Domain_Id ? String(customerObj.Domain_Id) : "",
            companyEmail: customerObj.Company_Email || "",
            companyPhone: stripPhoneCountryCode(customerObj.Company_Phone || ""),
            website: customerObj.Website || "",
            websiteDomainName: customerObj.Website_Domain_Name || "",
            source: sourceValue,
            status: customerObj.Status || "",
            communicationLanguage: customerObj.Com_Lang || "",
            isQuoteSent: !!customerObj.Is_Quote_Sent,
            portalEnabled: customerObj.Portal_Enabled != null ? !!customerObj.Portal_Enabled : true,
            hasPassword: !!customerObj.Has_Password,
            portalPassword: "",
            services: resolvedServices,
            addressLine1: addressObj.Address_Line_1 || "",
            addressLine2: addressObj.Address_Line_2 || "",
            city: addressObj.City_Name || "",
            state: addressObj.State_Name || "",
            country: addressObj.Country_Name || "",
            zipCode: addressObj.Zip_Code || "",
            companyAddressLine1: companyAddressObj.Address_Line_1 || "",
            companyAddressLine2: companyAddressObj.Address_Line_2 || "",
            companyCity: companyAddressObj.City_Name || "",
            companyState: companyAddressObj.State_Name || "",
            companyCountry: companyAddressObj.Country_Name || "",
            companyZipCode: companyAddressObj.Zip_Code || "",
            notes: customerObj.Notes || ""
          });
          if (addressObj.Country_Id) setSelectedCountryId(addressObj.Country_Id);
          if (addressObj.State_Id) setSelectedStateId(addressObj.State_Id);
          if (companyAddressObj.Country_Id) setSelectedCompanyCountryId(companyAddressObj.Country_Id);
          if (companyAddressObj.State_Id) setSelectedCompanyStateId(companyAddressObj.State_Id);

          try {
            // Use unified followups endpoint with client_id parameter
            const followupRes = await axiosInstance.get(
              `/followups?client_id=${clientId}`
            );
            if (
              followupRes.data &&
              Array.isArray(followupRes.data) &&
              followupRes.data.length > 0
            ) {
              const latest = followupRes.data[0];
              // Format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
              const formatDateTimeLocal = (dateString) => {
                if (!dateString) return "";
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return "";
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              };
              setFollowupData({
                followupDate: formatDateTimeLocal(latest.Followup_Date),
                followupType: latest.Followup_Type || latest.Type || "",
                nextFollowupDate: formatDateTimeLocal(latest.Next_Followup_Date),
                remarks: latest.Remarks || ""
              });
              // Store followup ID for update
              if (latest.Followup_Id) {
                setFollowupId(latest.Followup_Id);
              }
            }
          } catch (fErr) {
            console.error("Error fetching followups for customer:", fErr?.response?.data?.message || fErr?.message || fErr);
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Error fetching customer.";
        console.error("Error fetching customer:", msg);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer(serviceCategories);
  }, [clientId, serviceCategories]);

  useEffect(() => {
    if (serviceCategories.length === 0 || formData.services.length === 0)
      return;
    const firstService = formData.services[0];
    if (firstService && typeof firstService === "string") {
      const allSub = serviceCategories.flatMap((c) => c.subcategories || []);
      const ids = formData.services
        .map((name) => {
          let found = allSub.find((x) => x.subcategory_name === name);
          if (!found) {
            found = allSub.find((x) => 
              x.subcategory_name.toLowerCase() === name.toLowerCase()
            );
          }
          return found ? found.subcategory_id : null;
        })
        .filter(Boolean);
      
      if (ids.length > 0) {
        const currentIds = formData.services.filter(id => typeof id === 'number');
        const idsMatch = ids.length === currentIds.length && 
          ids.every(id => currentIds.includes(id));
        
        if (!idsMatch) {
          setFormData((prev) => ({ ...prev, services: ids }));
        }
      }
    }
  }, [serviceCategories, formData.services.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "country") {
      const c = countries.find((x) => x.country_name === value);
      if (c) setSelectedCountryId(c.country_id);
      else {
        setSelectedCountryId(null);
        setSelectedStateId(null);
        setStates([]);
        setCities([]);
        setFormData((prev) => ({ ...prev, state: "", city: "" }));
      }
    }
    if (name === "state") {
      const s = states.find((x) => x.state_name === value);
      if (s) setSelectedStateId(s.state_id);
      else {
        setSelectedStateId(null);
        setCities([]);
        setFormData((prev) => ({ ...prev, city: "" }));
      }
    }
    if (name === "companyCountry") {
      const c = countries.find((x) => x.country_name === value);
      if (c) setSelectedCompanyCountryId(c.country_id);
      else {
        setSelectedCompanyCountryId(null);
        setSelectedCompanyStateId(null);
        setCompanyStates([]);
        setCompanyCities([]);
        setFormData((prev) => ({ ...prev, companyState: "", companyCity: "" }));
      }
    }
    if (name === "companyState") {
      const s = companyStates.find((x) => x.state_name === value);
      if (s) setSelectedCompanyStateId(s.state_id);
      else {
        setSelectedCompanyStateId(null);
        setCompanyCities([]);
        setFormData((prev) => ({ ...prev, companyCity: "" }));
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleServiceChange = (subcategoryId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(subcategoryId)
        ? prev.services.filter((id) => id !== subcategoryId)
        : [...prev.services, subcategoryId]
    }));
  };

  const handleAddServiceToCategory = (categoryId) => {
    setEditingService({
      id: `custom-${Date.now()}`,
      name: "",
      categoryId
    });
    setIsAddingServiceToCategory(categoryId);
  };

  const handleSaveService = () => {
    if (!editingService || !editingService.name.trim()) return;
    setCustomServices((prev) => ({
      ...prev,
      [editingService.id]: { name: editingService.name.trim(), categoryId: editingService.categoryId }
    }));
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, editingService.id]
    }));
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleDeleteCustomService = (id) => {
    if (!window.confirm("Remove this custom service?")) return;
    setCustomServices((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s !== id)
    }));
  };

  const handleFollowupChange = (e) => {
    const { name, value } = e.target;
    setFollowupData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.personalPhone) {
      alert("Please fill in required fields (First Name and Phone).");
      return;
    }
    if (!formData.services || formData.services.length === 0) {
      alert("Please select at least one service.");
      return;
    }
    let createdBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }

    const selectedCountry = countries.find(
      (c) => c.country_name === formData.country
    );
    const selectedState = states.find((s) => s.state_name === formData.state);
    const selectedCity = cities.find((c) => c.city_name === formData.city);
    const selectedCompanyCountry = countries.find((c) => c.country_name === formData.companyCountry);
    const selectedCompanyState = companyStates.find((s) => s.state_name === formData.companyState);
    const selectedCompanyCity = companyCities.find((c) => c.city_name === formData.companyCity);

    // Source is sent as string (not Source_Id)
    const sourceName = formData.source || null;

    // Get Updated_By
    let updatedBy = createdBy;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || createdBy;
      }
    } catch (err) {
      console.error("Error reading user for Updated_By:", err);
    }

    const numericServiceIds = formData.services.filter((s) => typeof s === "number");
    const customServiceNames = formData.services
      .filter((s) => typeof s === "string")
      .map((id) => customServices[id]?.name)
      .filter(Boolean);

    const rawDomain = (formData.domain || "").toString().trim();
    let domainName = null;
    let domainId = null;
    if (rawDomain) {
      const match = domains.find((d) => String(d.id) === rawDomain);
      if (match) {
        domainName = (match.name || "").trim() || null;
        domainId = Number(match.id);
      } else if (/^\d+$/.test(rawDomain)) {
        domainId = parseInt(rawDomain, 10);
      } else {
        domainName = rawDomain;
      }
    }

    const payload = {
      Customer_Type: formData.customerType || "Organization",
      First_Name: formData.firstName,
      Last_Name: formData.lastName || null,
      Email: formData.personalEmail || null,
      Country_Code: personalPhoneCountryCode || null,
      Phone: (formData.personalPhone || "").trim() || null,
      Alt_Phone: (formData.altPhone || "").trim() || null,
      Company_Email: formData.companyEmail || null,
      Company_Name: formData.companyName || null,
      Company_Phone: (formData.companyPhone || "").trim() || null,
      Domain: domainName,
      ...(domainId != null && Number.isFinite(domainId) ? { Domain_Id: domainId } : {}),
      Source: sourceName,
      Status: formData.status || null,
      Portal_Enabled: formData.portalEnabled ? 1 : 0,
      ...(formData.portalPassword ? { Password: formData.portalPassword } : {}),
      Created_By: createdBy,
      Updated_By: updatedBy,
      services: numericServiceIds,
      ...(customServiceNames.length > 0 && { custom_services: customServiceNames }),
      Website: formData.website || null,
      Website_Domain_Name: formData.websiteDomainName || null,
      Com_Lang: (formData.communicationLanguage || "").trim() || null,
      Is_Quote_Sent: formData.isQuoteSent ? 1 : 0,
      Address_Line_1: formData.addressLine1 || null,
      Address_Line_2: formData.addressLine2 || null,
      City_Id: selectedCity ? selectedCity.city_id : null,
      State_Id: selectedState ? selectedState.state_id : selectedStateId,
      Country_Id: selectedCountry ? selectedCountry.country_id : selectedCountryId,
      Zip_Code: formData.zipCode || null,
      Company_Address_Line_1: formData.companyAddressLine1 || null,
      Company_Address_Line_2: formData.companyAddressLine2 || null,
      Company_City_Id: selectedCompanyCity ? selectedCompanyCity.city_id : null,
      Company_State_Id: selectedCompanyState ? selectedCompanyState.state_id : selectedCompanyStateId,
      Company_Country_Id: selectedCompanyCountry ? selectedCompanyCountry.country_id : selectedCompanyCountryId,
      Company_Zip_Code: formData.companyZipCode || null,
      Notes: formData.notes || null
    };

    setSubmitting(true);
    try {
      try {
        await axiosInstance.put(`/update-client/${clientId}`, payload);
      } catch (firstErr) {
        const status = firstErr?.response?.status;
        if (status === 404) {
          await axiosInstance.put(`/client/${clientId}`, payload);
        } else {
          throw firstErr;
        }
      }
      
      // Create/update followup if followup data is provided
      if (followupData.followupDate) {
        try {
          // Format dates to ISO string format (YYYY-MM-DDTHH:mm:ss)
          const formatDateTime = (dateTimeString) => {
            if (!dateTimeString) return null;
            // datetime-local input returns format: YYYY-MM-DDTHH:mm
            // API expects: YYYY-MM-DDTHH:mm:ss
            return dateTimeString + ":00";
          };

          const followupPayload = {
            Lead_Id: null,
            client_id: clientId,
            Followup_Date: formatDateTime(followupData.followupDate),
            Followup_Type: followupData.followupType || null,
            Remarks: followupData.remarks || null,
            Next_Followup_Date: formatDateTime(followupData.nextFollowupDate),
            Updated_By: updatedBy,
          };

          // If followup exists, update it; otherwise create new
          if (followupId) {
            await axiosInstance.put(`/update-followup/${followupId}`, followupPayload);
          } else {
            followupPayload.Created_By = updatedBy;
            await axiosInstance.post("/create-followup", followupPayload);
          }
        } catch (followupErr) {
          console.error("Error saving followup:", followupErr);
          // Don't fail the whole update if followup creation/update fails
        }
      }
      
      alert("Client updated successfully");
      if (onUpdate) onUpdate();
      if (onBack) onBack();
    } catch (err) {
      console.error("Error updating customer:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to update customer.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="editcustomer-container">
        <div className="editcustomer-loading">Loading customer details...</div>
      </div>
    );
  }

  return (
    <div className="editcustomer-container">
      <button className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back 
      </button>
      <div className="issues-main-card">
        <form className="editcustomer-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Customer type</label>
              <select
                name="customerType"
                value={formData.customerType}
                onChange={handleInputChange}
              >
                {CUSTOMER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Personal Email</label>
              <input
                type="email"
                name="personalEmail"
                value={formData.personalEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  style={{ maxWidth: "120px" }}
                  value={personalPhoneCountryCode}
                  onChange={(e) => setPersonalPhoneCountryCode(e.target.value)}
                  disabled={loadingCountries}
                >
                  <option value="">Code</option>
                  {countries.map((country) => {
                    const code = (country.country_code || country.country_short_code || "").trim();
                    return (
                      <option key={country.country_id} value={code}>
                        {code}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alternate phone</label>
              <input
                type="text"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Domain</label>
              {domains.length > 0 ? (
                <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                    <span>{loadingDomains ? "Loading domains..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select Domain") : "Select Domain")}</span>
                    <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                  </div>
                  {domainDropdownOpen && !loadingDomains && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search domains..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select Domain</div>
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                          <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id) })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                        ))}
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No domains found</div>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                />
              )}
            </div>
            <div className="form-group">
              <label>Company Email</label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Company Phone</label>
              <input
                type="text"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleInputChange}
                
              />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://www.example.com"
              />
            </div>
            <div className="form-group">
              <label>Website Domain Name</label>
              <input
                type="text"
                name="websiteDomainName"
                value={formData.websiteDomainName}
                onChange={handleInputChange}
                placeholder="example.com"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Client Address</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>City</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={!selectedStateId || loadingCities}
              >
                <option value="">{formData.city || "Select City"}</option>
                {cities.map((city) => (
                  <option key={city.city_id} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                disabled={!selectedCountryId || loadingStates}
              >
                <option value="">{formData.state || "Select State"}</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_name}>
                    {state.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Country</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={loadingCountries}
              >
                <option value="">{formData.country || "Select Country"}</option>
                {countries.map((country) => (
                  <option key={country.country_id} value={country.country_name}>
                    {country.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Source</label>
              <input
                type="text"
                name="source"
                value={formData.source}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="">Select status</option>
                {CUSTOMER_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Communication Language</label>
              <input
                type="text"
                name="communicationLanguage"
                value={formData.communicationLanguage}
                onChange={handleInputChange}
                
              />
            </div>
            <div className="form-group">
              <label>{formData.hasPassword ? "Reset Portal Password" : "Set Portal Password"}</label>
              <input
                type="password"
                name="portalPassword"
                value={formData.portalPassword}
                onChange={handleInputChange}
                placeholder={formData.hasPassword ? "Leave blank to keep unchanged" : "Optional — customer can also use OTP"}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <div style={{ height: "24px", marginBottom: "8px" }}></div>
              <div style={{ display: "flex", alignItems: "center", height: "42px" }}>
                <label style={{ marginRight: "8px", marginBottom: 0, cursor: "pointer" }} htmlFor="editPortalEnabled">Create Tenant</label>
                <input
                  id="editPortalEnabled"
                  type="checkbox"
                  name="portalEnabled"
                  checked={formData.portalEnabled}
                  onChange={handleInputChange}
                  style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#2977ed" }}
                />
              </div>
            </div>
            {formData.portalEnabled && (
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <h3 className="section-title" style={{ fontSize: "1rem", marginBottom: "12px" }}>Company Address</h3>
                <p style={{ marginTop: 0, marginBottom: "16px", color: "#64748b", fontSize: "14px" }}>
                  Office or business location for the tenant (separate from the client&apos;s personal address).
                </p>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      name="companyAddressLine1"
                      value={formData.companyAddressLine1}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="companyAddressLine2"
                      value={formData.companyAddressLine2}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <select
                      name="companyCountry"
                      value={formData.companyCountry}
                      onChange={handleInputChange}
                      disabled={loadingCountries}
                    >
                      <option value="">{formData.companyCountry || "Select Country"}</option>
                      {countries.map((country) => (
                        <option key={`company-${country.country_id}`} value={country.country_name}>
                          {country.country_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <select
                      name="companyState"
                      value={formData.companyState}
                      onChange={handleInputChange}
                      disabled={!selectedCompanyCountryId || loadingStates}
                    >
                      <option value="">{formData.companyState || "Select State"}</option>
                      {companyStates.map((state) => (
                        <option key={`company-${state.state_id}`} value={state.state_name}>
                          {state.state_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <select
                      name="companyCity"
                      value={formData.companyCity}
                      onChange={handleInputChange}
                      disabled={!selectedCompanyStateId || loadingCities}
                    >
                      <option value="">{formData.companyCity || "Select City"}</option>
                      {companyCities.map((city) => (
                        <option key={`company-${city.city_id}`} value={city.city_name}>
                          {city.city_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      name="companyZipCode"
                      value={formData.companyZipCode}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="form-section editcustomer-quote-row">
          <div className="form-grid">
            <div className="form-group editcustomer-quote">
              <label className="checkbox-label">
                <span className="editcustomer-quote-label">Quote Sent</span>
                <input
                  type="checkbox"
                  name="isQuoteSent"
                  checked={formData.isQuoteSent}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="btn-generate-quote"
                  onClick={() =>
                    navigate("/generate-quote", {
                      state: { customerData: formData, type: "client" }
                    })
                  }
                >
                  Generate Quote
                </button>
              </label>
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
             
                rows={4}
                style={{ resize: "vertical", minHeight: "100px", width: "98%" }}
              />
            </div>
          </div>
        </div>

        <div className="form-section editcustomer-section-divider">
          <h2 className="section-title">Services <span className="required">*</span></h2>
          {loadingServices ? (
            <p>Loading services...</p>
          ) : (
            <div className="services-card">
              {serviceCategories.map(category => {
                const subs = category.subcategories || [];
                const customForCategory = Object.entries(customServices)
                  .filter(([, s]) => s.categoryId === category.category_id)
                  .map(([id, s]) => ({ id, name: s.name }));
                return (
                  <div key={category.category_id} className="service-category">
                    <h3 className="category-title">{category.category_name}</h3>
                    <div className="services-grid">
                      {subs.map(sub => (
                        <label key={sub.subcategory_id} className="service-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.services.includes(sub.subcategory_id)}
                            onChange={() => handleServiceChange(sub.subcategory_id)}
                          />
                          <span>{sub.subcategory_name}</span>
                        </label>
                      ))}
                      {customForCategory.map(({ id, name }) => (
                        <div key={id} className="addlead-custom-service-row">
                          <label className="service-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.services.includes(id)}
                              onChange={() => handleServiceChange(id)}
                            />
                            <span>{name}</span>
                          </label>
                          <button
                            type="button"
                            className="addlead-custom-service-delete"
                            onClick={() => handleDeleteCustomService(id)}
                            title="Remove"
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="form-section editcustomer-section-divider">
          <h2 className="section-title">Latest Followup</h2>
          <div className="editcustomer-followup-grid">
            <div className="form-group">
              <label>Followup Date</label>
              <input
                type="datetime-local"
                name="followupDate"
                value={followupData.followupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Followup Type</label>
              <input
                type="text"
                name="followupType"
                value={followupData.followupType}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Next Followup Date</label>
              <input
                type="datetime-local"
                name="nextFollowupDate"
                value={followupData.nextFollowupDate}
                onChange={handleFollowupChange}
              />
            </div>
          </div>
          <div className="editcustomer-remarks-group">
            <label>Remarks</label>
            <textarea
              name="remarks"
              value={followupData.remarks}
              onChange={handleFollowupChange}
              rows={5}
            
            />
          </div>
        </div>

        <div className="editcustomer-actions">
          <button type="submit" className="btn-update-customer" disabled={submitting}>
            {submitting ? "Updating..." : "Update Client"}
          </button>
        </div>
      </form>
      </div>
      {editingService && (
        <div className="addlead-modal-overlay" onClick={handleCancelEditService}>
          <div className="addlead-modal addlead-add-service-modal" onClick={e => e.stopPropagation()}>
            <h3>{isAddingServiceToCategory ? `Add Service to ${serviceCategories.find(c => c.category_id === isAddingServiceToCategory)?.category_name || "Category"}` : "Edit Service"}</h3>
            <div className="form-group">
              <label>Service Name</label>
              <input
                type="text"
                value={editingService.name}
                onChange={e => setEditingService({ ...editingService, name: e.target.value })}
            
              />
            </div>
            <div className="addlead-modal-actions">
              <button type="button" className="btn-cancel" onClick={handleCancelEditService}>Cancel</button>
              <button type="button" className="btn-update-customer" onClick={handleSaveService} disabled={!editingService.name?.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditCustomer;
